/**
 * Party Search coordinator.
 *
 * Catalog ranking stays on searchCatalog. My Music is a separate private lane.
 * The React SearchBar must not rerank canonical results or call YouTube Search.
 */

import { getAuthUid } from '../firebase';
import { searchCatalog, SEARCH_UNAVAILABLE_MESSAGE } from './catalogSearch';
import { dedupeCatalogResults } from './catalogQueue';
import { searchMyMusicLibrary } from './myMusicSearch';

export const PARTY_SEARCH_EMPTY_MESSAGE = 'No matches found in Rhythmé yet.';

function preferMyMusicOverDuplicateCatalog(catalogResults, myMusicResults) {
  const privateKeys = new Set(
    (myMusicResults || [])
      .map((row) => row?.providerAssetKey)
      .filter((key) => typeof key === 'string' && key),
  );
  if (privateKeys.size === 0) return catalogResults;
  return catalogResults.filter((row) => (
    !row?.providerAssetKey || !privateKeys.has(row.providerAssetKey)
  ));
}

export async function searchPartyMusic(rawQuery, {
  uid = getAuthUid(),
  searchCatalogFn = searchCatalog,
  searchMyMusicFn = searchMyMusicLibrary,
} = {}) {
  const query = typeof rawQuery === 'string' ? rawQuery.trim() : '';
  if (!query) {
    return {
      status: 'empty',
      catalogResults: [],
      myMusicResults: [],
      confidenceClass: 'NO_MATCH',
      error: null,
      emptyMessage: PARTY_SEARCH_EMPTY_MESSAGE,
    };
  }

  const [catalogOutcome, myMusicOutcome] = await Promise.all([
    searchCatalogFn(query),
    Promise.resolve().then(() => searchMyMusicFn(query, { uid })).catch(() => ({
      results: [],
      limited: true,
    })),
  ]);

  const myMusicResults = Array.isArray(myMusicOutcome?.results)
    ? myMusicOutcome.results
    : [];

  if (!catalogOutcome?.ok) {
    return {
      status: 'unavailable',
      catalogResults: [],
      myMusicResults,
      confidenceClass: null,
      error: SEARCH_UNAVAILABLE_MESSAGE,
      emptyMessage: null,
    };
  }

  const catalogResults = preferMyMusicOverDuplicateCatalog(
    dedupeCatalogResults(catalogOutcome.data?.results),
    myMusicResults,
  );
  const confidenceClass = catalogOutcome.data?.confidenceClass || null;
  const hasHits = catalogResults.length > 0 || myMusicResults.length > 0;

  return {
    status: hasHits ? 'ok' : 'empty',
    catalogResults,
    myMusicResults,
    confidenceClass,
    error: null,
    emptyMessage: hasHits ? null : PARTY_SEARCH_EMPTY_MESSAGE,
  };
}
